import {
  getUserNamesOptions,
  updateBadgeTemplate,
  useCompaniesNames,
  useTemplateAssets,
} from "@/api";
import { TemplatePreview } from "@/components/badge_printing/TemplatePreview";
import PageBackButton from "@/components/PageBackButton";
import { Breadcrumbs } from "@app-components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import styles from "@/styles/TemplatePreview.module.scss";
import { useState } from "react";
import { usePrompt } from "@/hooks/usePrompt";
import { BadgeTemplateMetadata, NotificationType } from "@/types";
import { useForm } from "@tanstack/react-form";
import SaveResetButtons from "@/components/SaveResetButtons";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbarStore } from "@/store/zustandStore";
import { TemplateNameField } from "@/components/badge_printing/TemplateNameField";
import { templateNameValidation } from "@/components/badge_printing/utils/templateNameValidation";
import { AssignedToCompaniesField } from "@/components/badge_printing/AssignedToCompanyField";
import { formatTimestampToTimeAgo, getBuildingId } from "@/lib";
import i18n from "@/i18n/config";
import { useBuildingQuery } from "@/components/RouterSetup";

export const Route = createFileRoute(
  "/_portal/building/settings/template/$templateId",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { templateId } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { addNotification } = useSnackbarStore();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { data: templateData } = useTemplateAssets(templateId);
  const { data: companyNames } = useCompaniesNames([], {
    getAllIfNoCompanyIds: true,
    noExternal: true,
  });
  const { data: createdByName } = useBuildingQuery(
    getUserNamesOptions(true, [templateData?.metadata.createdBy]),
  );

  usePrompt({
    isOpen: hasUnsavedChanges,
  });

  const assets = templateData.assets;
  const metadata = templateData.metadata;

  const companyOptions = [
    ...companyNames.map((company) => ({
      label: company.buildingTenantName ?? "",
      value: company.buildingTenantId ?? "",
    })),
  ];

  const breadcrumbs = [
    {
      label: t("SIDEBAR.BUILDING_SETTINGS"),
      path: "/building/settings",
    },
    {
      label: "Welcome Access",
      path: "/building/settings/access",
    },
    {
      label: t("BADGE_PRINTING.TEMPLATES.TEMPLATE"),
      path: `/building/settings/template/${templateId}`,
    },
  ];

  const form = useForm<Partial<BadgeTemplateMetadata>>({
    defaultValues: {
      templateName: metadata.templateName,
      assignedToCompanies: metadata.assignedToAllCompanies
        ? ["__ALL__", ...companyOptions.map((c) => c.value)]
        : metadata.assignedToCompanies,
      assignedToAllCompanies: metadata.assignedToAllCompanies,
    },
    onSubmit: async (values) => {
      const isAll = (values.value.assignedToCompanies ?? []).includes(
        "__ALL__",
      );

      const finalData = {
        ...values.value,
        assignedToCompanies: isAll ? [] : values.value.assignedToCompanies,
        assignedToAllCompanies: isAll,
      };
      try {
        await updateBadgeTemplate(templateId, finalData);
        addNotification(
          t("BADGE_PRINTING.TEMPLATE_FORM.UPDATED_SUCCESSFULLY"),
          NotificationType.SUCCESS,
        );
        setHasUnsavedChanges(false);
        queryClient.invalidateQueries({
          queryKey: [getBuildingId(), "badge-templates"],
        });
        queryClient.invalidateQueries({
          queryKey: [getBuildingId(), "template-assets", templateId],
        });
        queryClient.invalidateQueries({
          queryKey: [getBuildingId(), "templates-for-company"],
        });
      } catch (error) {
        console.error("Failed to update template:", error);
        addNotification(t("BADGE_PRINTING.TEMPLATE_FORM.UPDATE_FAILED"));
        return;
      }
    },
  });

  return (
    <>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton
          fallbackPath={{
            to: "/building/settings/access",
          }}
        />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <form
        onChange={() => setHasUnsavedChanges(true)}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className={styles.formContainer}>
          <form.Field
            name="templateName"
            validators={templateNameValidation(t)}
          >
            {(field) => <TemplateNameField field={field} />}
          </form.Field>
          <form.Field name="assignedToCompanies">
            {(field) => (
              <AssignedToCompaniesField
                field={field}
                form={form}
                companyOptions={companyOptions}
                onFieldChange={() => setHasUnsavedChanges(true)}
              />
            )}
          </form.Field>
          <div className={styles.infoBox}>
            <p>
              {t("BADGE_PRINTING.TABLE.CREATED_AT")}:{" "}
              <strong>
                {formatTimestampToTimeAgo(
                  t,
                  metadata.createdAt,
                  i18n.language,
                  true,
                )}
              </strong>
            </p>
            <p>
              {t("BADGE_PRINTING.TABLE.CREATED_BY")}:{" "}
              <strong>{createdByName?.[metadata.createdBy]}</strong>
            </p>
          </div>
        </div>

        <form.Subscribe
          selector={(state) => ({
            assignedToCompanies: state.values.assignedToCompanies ?? [],
            assignedToAllCompanies: state.values.assignedToAllCompanies,
          })}
        >
          {({ assignedToCompanies, assignedToAllCompanies }) => {
            const isSubmitDisabled = !(
              assignedToAllCompanies || assignedToCompanies.length > 0
            );

            return (
              <SaveResetButtons
                form={form}
                showButtons={hasUnsavedChanges}
                onHideButtons={() => setHasUnsavedChanges(false)}
                disabledSubmit={isSubmitDisabled}
              />
            );
          }}
        </form.Subscribe>
      </form>
      <TemplatePreview
        frontHtml={
          new File([assets.frontHtml], "front.html", { type: "text/html" })
        }
        backHtml={
          assets.backHtml
            ? new File([assets.backHtml], "back.html", { type: "text/html" })
            : null
        }
        cssFile={
          assets.css
            ? new File([assets.css], "styles.css", { type: "text/css" })
            : null
        }
        backgroundImageFront={assets.backgroundImageFront}
        backgroundImageBack={assets.backgroundImageBack}
      />
    </>
  );
}
